/* eslint-disable react-hooks/exhaustive-deps */
import { Card } from '@components/card/card';
import NoData from '@components/NoData/NoData';
import Loading from '@components/Shared/Loader/Loading';
import { DynamicPagination } from '@Helper/DynamicFilter/DynamicPagination';

import { splitBarcodes, TABLE_KEY, type PriceListBarcodeVM } from './usePriceListBarcodePrint';

type Props = { vm: PriceListBarcodeVM };

export default function ItemsColumn({ vm }: Props) {
  return (
    <div className='col-lg-7 mb-2'>
      <Card className='shadow-sm h-100'>
        {/* Header: PriceList Select */}
        <div className='p-3'>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <label className='form-label fw-semibold mb-0'>
              <i className='bi bi-list-check me-2'></i>
              قائمة السعر
              <span className='text-danger ms-1'>*</span>
            </label>

            {vm.priceListLoading && (
              <div className='spinner-border spinner-border-sm text-theme' role='status'>
                <span className='visually-hidden'>جاري التحميل...</span>
              </div>
            )}
          </div>

          <div className='input-group'>
            <select
              className='form-select'
              value={vm.selectedPriceListId}
              onChange={e => vm.setSelectedPriceListId(e.target.value)}
              disabled={vm.priceListLoading}
              aria-label='اختر قائمة السعر'
            >
              <option value=''>— اختر قائمة السعر —</option>
              {vm.priceLists.map((pl: any) => (
                <option key={pl?.id} value={pl?.id}>
                  {vm.getPLName(pl)}
                  {pl?.code ? ` (${pl.code})` : ''}
                </option>
              ))}
            </select>

            {vm.priceListError ? (
              <span className='input-group-text bg-danger bg-opacity-10 text-danger border-danger'>
                <i className='bi bi-exclamation-triangle me-1'></i>
                خطأ
              </span>
            ) : null}
          </div>
        </div>

        <div className='card-body d-flex flex-column justify-content-start p-0'>
          {/* Tabs header */}
          <div className='border-bottom'>
            <div className='d-flex justify-content-between align-items-center px-4 pt-3'>
              <div>
                <h6 className='mb-1'>
                  <i className='bi bi-box-seam me-2'></i>
                  قائمة الأصناف
                </h6>
              </div>

              <div className='btn-group btn-group' role='group'>
                <button
                  type='button'
                  className={`btn ${vm.activeTab === 'items' ? 'btn-theme' : 'btn-outline-secondary'}`}
                  onClick={() => vm.setActiveTab('items')}
                >
                  <i className='bi bi-grid me-1'></i>
                  الأصناف
                </button>

                <button
                  type='button'
                  className={`btn ${vm.activeTab === 'selected' ? 'btn-theme' : 'btn-outline-secondary'}`}
                  onClick={() => vm.setActiveTab('selected')}
                >
                  <i className='bi bi-check-circle me-1'></i>
                  المحدد
                  <span className='badge bg-success bg-opacity-25 text-success ms-1'>{vm.selectedItems.length}</span>
                </button>
              </div>
            </div>

            {/* Search bar */}
            {vm.selectedPriceListId && vm.activeTab === 'items' && (
              <div className='px-4 py-3 bg-secondary bg-opacity-25'>
                <div className='row g-2 align-items-center'>
                  <div className='col-auto' style={{ width: '140px' }}>
                    <div className='input-group input-group-sm'>
                      <select
                        className='form-select'
                        value={vm.filterKey}
                        onChange={e => vm.setFilterKey(e.target.value as any)}
                        style={{ fontSize: '0.875rem' }}
                      >
                        {/* نفس الخيارات عندك */}
                        <option value='barcode'>الباركود</option>
                        <option value='name'>الاسم</option>
                        <option value='itemCode'>كود الصنف</option>
                      </select>
                    </div>
                  </div>

                  <div className='col'>
                    <div className='input-group input-group-sm shadow-sm'>
                      <input
                        className='form-control'
                        value={vm.filterText}
                        onChange={e => vm.setFilterText(e.target.value)}
                        placeholder='بحث...'
                        onKeyDown={e => e.key === 'Enter' && vm.applySearch()}
                        style={{ borderRight: 0 }}
                      />

                      <button className='btn btn-theme' type='button' onClick={vm.applySearch} disabled={vm.loading}>
                        {vm.loading ? (
                          <span className='spinner-border spinner-border-sm' role='status'>
                            <span className='visually-hidden'>جاري البحث...</span>
                          </span>
                        ) : (
                          <>
                            <i className='bi bi-search'></i>
                            <span className='d-none d-md-inline ms-1'>بحث</span>
                          </>
                        )}
                      </button>

                      {vm.filterText && (
                        <button
                          className='btn btn-outline-secondary'
                          type='button'
                          onClick={vm.clearSearch}
                          disabled={vm.loading}
                          title='مسح البحث'
                        >
                          <i className='bi bi-x'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Body */}
          <div className='flex-grow-1 px-4 pb-4'>
            {vm.activeTab === 'items' ? (
              <>
                {!vm.selectedPriceListId ? (
                  <div className='d-flex flex-column align-items-center justify-content-center h-100 text-center py-5'>
                    <div className='bg-theme-light bg-opacity-10 rounded-circle p-4 mb-3'>
                      <i className='bi bi-list-check display-4 text-theme opacity-50'></i>
                    </div>
                    <h5 className='mb-2'>اختر قائمة سعر</h5>
                    <p className='text-muted mb-0 small'>اختر قائمة سعر من القائمة أعلاه لعرض الأصناف المتاحة</p>
                  </div>
                ) : vm.loading ? (
                  <Loading loading={vm.loading} Type='Dots' children={<></>} />
                ) : vm.data.length ? (
                  <>
                    <div className='table-responsive mt-3' style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <table className='table table-hover table-sm mb-0'>
                        <thead className='sticky-top' style={{ top: 0, zIndex: 1 }}>
                          <tr className='bg-theme bg-opacity-40' style={{ backdropFilter: 'blur(20px)' }}>
                            <th className='text-muted border-0 py-3 ps-3'>
                              <i className='bi bi-list-check me-1'></i>
                              الأصناف
                            </th>
                            <th className='small text-muted border-0 py-3 text-end pe-3'>الوحدة</th>
                          </tr>
                        </thead>

                        <tbody>
                          {vm.data.map((item: any, idx: number) => {
                            const k = `${item?.itemId}-${item?.unitOfMeasure}`;
                            const selected = !!vm.selectedMap[k];
                            const barcodes = splitBarcodes(item?.barcode);
                            const price = vm.getUnitPriceForPrint(item);

                            return (
                              <tr
                                key={k || idx}
                                className={`${selected ? 'table-success' : ''} transition-all cursor-pointer`}
                                onClick={() => vm.toggleSelect(item)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td className='ps-3'>
                                  <div className='d-flex align-items-center gap-3'>
                                    <div className={`rounded-circle p-1 ${selected ? 'bg-success text-white' : 'bg-theme'}`}>
                                      {selected ? (
                                        <i className='bi bi-check-lg' style={{ fontSize: '0.8rem', width: 16, height: 16 }} />
                                      ) : (
                                        <div style={{ width: 16, height: 16 }} />
                                      )}
                                    </div>

                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div className='d-flex justify-content-between align-items-start'>
                                        <div className='fw-semibold text-truncate' style={{ maxWidth: '350px' }}>
                                          {vm.getDisplayName(item)}
                                        </div>

                                        {price > 0 && (
                                          <div className='badge bg-theme bg-opacity-10 text-theme ms-2 fs-6 fw-bold'>
                                            {price.toFixed(2)} {vm.currency}
                                          </div>
                                        )}
                                      </div>

                                      <div className='text-theme mt-1'>
                                        {item?.itemCode && (
                                          <span className='me-3'>
                                            <i className='bi bi-tag me-1'></i>
                                            {item.itemCode}
                                          </span>
                                        )}

                                        {barcodes.length > 0 && (
                                          <span className={`me-3 ${barcodes.length > 1 ? 'text-warning' : 'text-muted'}`}>
                                            <i className='bi bi-upc-scan me-1'></i>
                                            {barcodes.length} باركود
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                <td className='text-end align-middle pe-3'>
                                  <span className='badged bg-theme-light text-theme' title={vm.getDisplayUom(item)}>
                                    {vm.getDisplayUom(item)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className='mt-3 pt-3 border-top'>
                      <DynamicPagination tableKey={TABLE_KEY} maxVisiblePages={0} showItemsPerPage={false} />
                    </div>
                  </>
                ) : (
                  <div className='d-flex flex-column align-items-center justify-content-center h-100 text-center py-5'>
                    <NoData />
                    {vm.filterText && (
                      <div className='mt-3'>
                        <button className='btn btn-outline-secondary btn-sm' onClick={vm.clearSearch}>
                          <i className='bi bi-arrow-counterclockwise me-1'></i>
                          عرض كل الأصناف
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              // Selected tab
              <div className='py-3'>
                {vm.selectedItems.length === 0 ? (
                  <div className='text-center py-5'>
                    <div className='bg-light bg-opacity-25 rounded-circle p-4 d-inline-block mb-3'>
                      <i className='bi bi-tags display-5 text-muted'></i>
                    </div>
                    <h5 className='text-muted'>لا توجد أصناف محددة</h5>
                    <p className='text-muted small mb-0'>انقر على أي صنف من قائمة الأصناف لتحديده</p>
                  </div>
                ) : (
                  <>
                    <div className='alert alert-success d-flex align-items-center mb-3'>
                      <i className='bi bi-info-circle me-2'></i>
                      <div className='small'>
                        تم تحديد <strong>{vm.selectedItems.length}</strong> صنف، وسيتم طباعة{' '}
                        <strong>{vm.totalBarcodes}</strong> ملصق
                      </div>
                    </div>

                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <div className='row g-1'>
                        {vm.selectedItems.map((it: any) => {
                          const k = `${it?.itemId}-${it?.unitOfMeasure}`;
                          const barcodes = splitBarcodes(it?.barcode);
                          const currentPick = vm.barcodePickMap[k] ?? barcodes[0] ?? '';
                          const qty = vm.qtyMap[k] ?? 1;

                          return (
                            <div key={k} className='col-12 p-3 m-0'>
                              <Card>
                                <div className='card hover-shadow-sm transition-all'>
                                  <div className='card-body p-3'>
                                    <div className='d-flex align-items-center justify-content-between'>
                                      <div className='d-flex align-items-center gap-3' style={{ flex: 1 }}>
                                        <button className='btn btn-outline-danger' onClick={() => vm.removeSelected(k)}>
                                          <i className='bi bi-x fa-lg'></i>
                                        </button>

                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          <div className='d-flex justify-content-between align-items-start mb-1'>
                                            <h6 className='mb-0 fw-semibold text-truncate' style={{ maxWidth: '200px' }}>
                                              {vm.getDisplayName(it)}
                                            </h6>
                                            <div className='text-success fw-bold'>
                                              {vm.getUnitPriceForPrint(it).toFixed(2)} {vm.currency}
                                            </div>
                                          </div>

                                          <div className='d-flex align-items-center justify-content-between'>
                                            <div className='small text-muted'>
                                              <span className='me-3'>
                                                <i className='bi bi-tag me-1'></i>
                                                {it?.itemCode}
                                              </span>
                                              <span>
                                                <i className='bi bi-rulers me-1'></i>
                                                {vm.getDisplayUom(it)}
                                              </span>
                                            </div>

                                            <div className='d-flex align-items-center gap-2'>
                                              {/* Barcode pick */}
                                              {barcodes.length > 0 && (
                                                <select
                                                  className='form-select form-select-sm'
                                                  style={{ width: '150px', fontSize: '0.75rem' }}
                                                  value={vm.printAllBarcodes ? '__ALL__' : currentPick || (barcodes[0] ?? '')}
                                                  disabled={vm.printAllBarcodes || barcodes.length === 1}
                                                  onChange={e => vm.setBarcodePickMap(prev => ({ ...prev, [k]: e.target.value }))}
                                                >
                                                  {barcodes.length > 1 && <option value='__ALL__'>كل الباركودات</option>}
                                                  {barcodes.map(b => (
                                                    <option key={b} value={b}>
                                                      {b.length > 20 ? `${b.substring(0, 20)}...` : b}
                                                    </option>
                                                  ))}
                                                </select>
                                              )}

                                              {/* Qty */}
                                              <div className='input-group input-group-sm' style={{ width: '100px' }}>
                                                <button
                                                  className='btn btn-outline-secondary'
                                                  type='button'
                                                  onClick={() =>
                                                    vm.setQtyMap(prev => ({ ...prev, [k]: Math.max(1, (prev[k] ?? 1) - 1) }))
                                                  }
                                                >
                                                  <i className='bi bi-dash'></i>
                                                </button>

                                                <input
                                                  type='text'
                                                  className='form-control text-center'
                                                  value={qty}
                                                  onChange={e => {
                                                    const v = Math.max(1, Math.floor(Number(e.target.value || 1)));
                                                    vm.setQtyMap(prev => ({ ...prev, [k]: v }));
                                                  }}
                                                  style={{ fontSize: '0.75rem' }}
                                                />

                                                <button
                                                  className='btn btn-outline-secondary'
                                                  type='button'
                                                  onClick={() => vm.setQtyMap(prev => ({ ...prev, [k]: (prev[k] ?? 1) + 1 }))}
                                                >
                                                  <i className='bi bi-plus'></i>
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
